import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import ConfirmDialog from '../common/ConfirmDialog';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';

const CATEGORY_LABELS = {
  music: 'Música',
  program: 'Programa',
  advertising: 'Publicidad',
  jingle: 'Jingle',
  promo: 'Promo',
  playlist: 'Playlist',
  special: 'Especial',
  fallback: 'Fallback',
};

export default function FolderList() {
  const [folders, setFolders] = useState([]);
  const [exhaust, setExhaust] = useState({});
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);

  useEffect(() => {
    fetchFolders();
    fetchExhaust();
  }, [categoryFilter]);

  useEffect(() => {
    const id = setInterval(fetchExhaust, 60000);
    return () => clearInterval(id);
  }, []);

  const fetchFolders = async () => {
    setLoading(true);
    try {
      const params = categoryFilter !== 'all' ? { category: categoryFilter } : {};
      const { data } = await api.get('/api/music/folders', { params });
      setFolders(data || []);
    } catch (err) {
      console.error('[FolderList] Failed to fetch folders:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchExhaust = async () => {
    try {
      const { data } = await api.get('/api/music/folders/exhaustion');
      const map = {};
      (data || []).forEach((r) => { map[r.folder_id] = r; });
      setExhaust(map);
    } catch (err) {
      console.error('[FolderList] Failed to fetch exhaustion:', err);
    }
  };

  const handleDelete = async (folder) => {
    try {
      await api.delete(`/api/music/folders/${folder.id}`);
      setFolders(folders.filter((f) => f.id !== folder.id));
    } catch (err) {
      console.error('[FolderList] Delete failed:', err);
    }
  };

  const confirmDelete = () => {
    if (confirmItem) {
      handleDelete(confirmItem);
      setConfirmOpen(false);
      setConfirmItem(null);
    }
  };

  const confirmUpdate = async (folder) => {
    setConfirmingId(folder.id);
    try {
      await api.post(`/api/music/folders/${folder.id}/confirm-update`);
      fetchFolders();
    } catch (err) {
      console.error('[FolderList] Confirm update failed:', err);
      alert('No se pudo marcar como actualizada.');
    } finally {
      setConfirmingId(null);
    }
  };

  const filteredFolders = folders.filter((f) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (f.name || '').toLowerCase().includes(q) ||
      (f.description || '').toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Cargando carpetas...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <style>{`
        @keyframes folderPulseNear { 0%,100% { box-shadow: 0 0 0 0 rgba(245,158,11,0.75); } 50% { box-shadow: 0 0 0 4px rgba(245,158,11,0); } }
        @keyframes folderPulseExhausted { 0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.85); } 50% { box-shadow: 0 0 0 5px rgba(239,68,68,0); } }
        .folder-near { border-color: #f59e0b !important; animation: folderPulseNear 1.2s ease-in-out infinite; }
        .folder-exhausted { border-color: #ef4444 !important; animation: folderPulseExhausted 0.9s ease-in-out infinite; }
      `}</style>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">Carpetas de música</h1>
        <Link to="/music/folders/create" className="btn btn-primary btn-sm">
          + Nueva Carpeta
        </Link>
      </div>
      <p className="text-xs text-dark-500">
        🔴 Las que <span className="text-error-400 font-semibold">parpadean en rojo</span> ya reprodujeron todas sus canciones en las últimas 48h · 🟠 las que <span className="text-amber-400 font-semibold">parpalean en ámbar</span> están por agotarse.
      </p>
      <p className="text-xs text-dark-500">
        📅 cada carpeta muestra su <span className="text-dark-200 font-medium">última actualización</span> · <span className="text-error-400 font-semibold">⚠ Sin actualizar desde que sonó</span> = necesita contenido nuevo (no se subieron archivos desde su última emisión en la radio).
      </p>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-dark-500">
            🔍
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar carpeta por nombre..."
            className="input w-full pl-9 pr-8"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute inset-y-0 right-2 flex items-center px-1 text-sm text-dark-400 hover:text-dark-200"
              title="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCategoryFilter('all')}
            className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${
              categoryFilter === 'all' ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
            }`}
          >
            Todas
          </button>
          {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
            <button
              key={value}
              onClick={() => setCategoryFilter(categoryFilter === value ? 'all' : value)}
              className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${
                categoryFilter === value ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {folders.length === 0 ? (
        <div className="py-12 text-center text-dark-400">
          No hay carpetas. Creá una para empezar a organizar la música por estilo.
        </div>
      ) : filteredFolders.length === 0 ? (
        <div className="py-12 text-center text-dark-400">
          No se encontraron carpetas para "<span className="text-dark-200">{search}</span>".
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {filteredFolders.map((folder) => {
            const lu = folder.last_updated ? parseISO(folder.last_updated) : null;
            const lc = folder.last_content_update ? parseISO(folder.last_content_update) : null;
            const lm = folder.last_confirmed_update ? parseISO(folder.last_confirmed_update) : null;
            const lp = folder.last_played ? parseISO(folder.last_played) : null;
            const stale = !!lp && (!lu || lu < lp);
            const manualConfirmed = !!(lm && lc && lm > lc);
            const staleDays = lp ? differenceInCalendarDays(new Date(), lp) : 0;
            const updatedDays = lu ? differenceInCalendarDays(new Date(), lu) : null;
            return (
            <div
              key={folder.id}
              className={`flex flex-col overflow-hidden rounded-xl bg-dark-850 border border-dark-700 transition-transform hover:scale-[1.02] ${
                exhaust[folder.id]?.status === 'near'
                  ? 'folder-near'
                  : exhaust[folder.id]?.status === 'exhausted'
                  ? 'folder-exhausted'
                  : ''
              }`}
            >
              <Link to={`/music/folders/${folder.id}`} className="block">
                <div className="relative aspect-[4/5] w-full overflow-hidden bg-dark-800">
                  {folder.image_url ? (
                    <img src={folder.image_url} alt={folder.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-4xl">📁</div>
                  )}
                  {exhaust[folder.id]?.status === 'exhausted' && (
                    <div className="absolute inset-x-0 top-0 bg-error-600/90 py-1 text-center text-[11px] font-bold text-white">
                      ⛔ AGOTADA
                    </div>
                  )}
                </div>
              </Link>
              <div className="flex flex-1 flex-col p-3">
                <div className="flex items-start justify-between gap-1">
                  <Link
                    to={`/music/folders/${folder.id}`}
                    className="text-sm font-semibold text-dark-100 hover:text-primary-400"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {folder.name}
                  </Link>
                  <div className="flex shrink-0 items-center gap-2">
                    <Link
                      to={`/music/folders/${folder.id}/edit`}
                      className="rounded-md bg-dark-700 px-2 py-1.5 text-xs text-dark-300 hover:bg-dark-600 hover:text-dark-100"
                      title="Editar carpeta"
                    >
                      ✎
                    </Link>
                    <button
                      onClick={() => {
                        setConfirmItem(folder);
                        setConfirmOpen(true);
                      }}
                      className="rounded-md bg-dark-700 px-2 py-1.5 text-xs text-error-400 hover:bg-error-900/40 hover:text-error-300"
                      title="Eliminar carpeta"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                {folder.description && (
                  <p
                    className="mt-1 text-xs text-dark-500"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {folder.description}
                  </p>
                )}
                <div className="mt-2">
                  <span
                    className="inline-block text-[11px] font-medium px-2 py-0.5 rounded"
                    style={{ backgroundColor: folder.color || '#27272a', color: '#f4f4f5' }}
                  >
                    {CATEGORY_LABELS[folder.category] || folder.category}
                  </span>
                </div>
                {lu ? (
                  <p className="mt-1.5 text-[11px] text-dark-400" title={format(lu, 'dd/MM/yyyy HH:mm')}>
                    📅 Actualizada: {format(lu, 'dd/MM/yyyy')}
                    {updatedDays != null && updatedDays > 0 && <span className="text-dark-500"> · hace {updatedDays} d</span>}
                  </p>
                ) : (
                  <p className="mt-1.5 text-[11px] text-dark-600">📅 Sin archivos todavía</p>
                )}
                {stale && (
                  <p
                    className="mt-1.5 rounded bg-error-900/30 border border-error-600/50 px-1.5 py-1 text-[11px] font-semibold text-error-300"
                    title={lp ? `Última emisión en la radio: ${format(lp, 'dd/MM/yyyy HH:mm')}` : ''}
                  >
                    ⚠ Sin actualizar desde que sonó · {staleDays} d
                  </p>
                )}
                {stale && (
                  <button
                    type="button"
                    onClick={() => confirmUpdate(folder)}
                    disabled={confirmingId === folder.id}
                    className="mt-1.5 w-full rounded bg-success-700/40 border border-success-600/50 px-1.5 py-1 text-[11px] font-semibold text-success-300 hover:bg-success-700/60 disabled:opacity-50"
                    title="Marcar como actualizada (contará como actualizada aunque repitas el programa)"
                  >
                    {confirmingId === folder.id ? '✓ Marcando…' : '✓ Marcar actualizada'}
                  </button>
                )}
                {!stale && lp && lu && (
                  <p className="mt-1.5 text-[11px] text-success-400/80">
                    {manualConfirmed
                      ? `✓ Marcada manualmente ${format(lm, 'dd/MM/yyyy')}`
                      : '✓ Contenido nuevo desde su última emisión'}
                  </p>
                )}
                <div className="mt-auto flex items-center justify-between border-t border-dark-700 pt-2.5 mt-3">
                  <span className="text-xs text-dark-400">
                    🎵 {parseInt(folder.file_count, 10) || 0}
                    {exhaust[folder.id]?.status === 'near' && (
                      <span className="ml-2 font-semibold text-amber-400">
                        ⚠ {exhaust[folder.id].remaining} libres
                      </span>
                    )}
                    {exhaust[folder.id]?.status === 'exhausted' && (
                      <span className="ml-2 font-semibold text-error-400">⛔ 0 libres</span>
                    )}
                  </span>
                  <Link
                    to={`/music/folders/${folder.id}`}
                    className="text-xs text-primary-400 hover:text-primary-300"
                  >
                    Ver →
                  </Link>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Eliminar Carpeta"
        message={`¿Seguro que quieres eliminar "${confirmItem?.name}"? Esta acción no se puede deshacer.`}
        danger
        onConfirm={confirmDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}
